import { useMemo } from 'react';
import type { GetAgendaRecordsRes, JoinedFilteredAgendaRes, AgendaRecordItem, FlattenedJoinedAgendaRecord } from 'types';
import { joinedTableRowId } from './joinedListing';

export interface UseFlattenFilteredRecordsProps {
  filteredData: GetAgendaRecordsRes<JoinedFilteredAgendaRes[]>;
}

const toRecordRef = (record: AgendaRecordItem | null): FlattenedJoinedAgendaRecord['nestedRecord'] =>
  record ? { id: record.id, agendaId: record.agendaId } : null;

/**
 * Flattens joined records into table rows.
 * Nested attributes are copied onto the main record. Nested record refs stay on the row
 * so later saves and calculations can target the nested record, and nestedRecordId /
 * nestedAgendaId stay available so a row click can open that nested record.
 * `id` stays the parent record so selection and export keep targeting it. `rowId`
 * distinguishes joined rows that share the same parent id.
 */
export const useFlattenFilteredRecords = ({ filteredData }: UseFlattenFilteredRecordsProps): GetAgendaRecordsRes<FlattenedJoinedAgendaRecord[]> => {
  return useMemo(() => {
    if (!filteredData?.items) {
      return {
        items: [],
        page: filteredData?.page ?? 1,
        pageSize: filteredData?.pageSize ?? 10,
        totalCount: filteredData?.totalCount ?? 0,
        hasPreviousPage: filteredData?.hasPreviousPage ?? false,
        hasNextPage: filteredData?.hasNextPage ?? false,
      };
    }

    const seenRowIds = new Map<string, number>();

    return {
      items: filteredData.items.map((joinedRecord) => {
        const nestedRecord = toRecordRef(joinedRecord.nestedRecord);
        const secondLevelNestedRecord = toRecordRef(joinedRecord.secondLevelNestedRecord);
        const baseRowId = joinedTableRowId(joinedRecord.mainRecord.id, nestedRecord?.id, secondLevelNestedRecord?.id);
        const seenCount = seenRowIds.get(baseRowId) ?? 0;
        seenRowIds.set(baseRowId, seenCount + 1);

        return {
          ...joinedRecord.mainRecord,
          attributes: [
            ...joinedRecord.mainRecord.attributes,
            ...(joinedRecord.nestedRecord?.attributes ?? []),
            ...(joinedRecord.secondLevelNestedRecord?.attributes ?? []),
          ],
          // Suffix follows response order. A refetch that reorders duplicate join keys can rebind a dirty row id.
          rowId: seenCount === 0 ? baseRowId : `${baseRowId}__${seenCount}`,
          nestedRecord,
          secondLevelNestedRecord,
          ...(nestedRecord
            ? {
                nestedRecordId: nestedRecord.id,
                nestedAgendaId: nestedRecord.agendaId,
              }
            : {}),
        };
      }),
      page: filteredData.page,
      pageSize: filteredData.pageSize,
      totalCount: filteredData.totalCount,
      hasPreviousPage: filteredData.hasPreviousPage,
      hasNextPage: filteredData.hasNextPage,
    };
  }, [filteredData]);
};
